import { useLingui } from '@lingui/react';
import { Box, Group, Stack, Text } from '@mantine/core';
import EventItem, { EventItemMultiple } from './EventItem';
import { ThemeText } from '../../Atoms/ThemeText';
import { useContext } from 'react';
import { EventsContext } from '../../Service/Context/EventsContext';

const MS_IN_DAY = 24 * 60 * 60 * 1000;

const EventsMonthPane = ({ month, year, eventSums, eventDates }) => {
  const { i18n } = useLingui();
  const { filter } = useContext(EventsContext) ?? {};
  const { agencyModeActive } = filter ?? { agencyModeActive: false };
  const { length } = eventDates;
  const monthTitle = month === 1 && year === 1970 ? 'Ohne Datum' : `${i18n._(`month.${month}`)} ${year}`;

  if (length === 0) {
    return null;
  }

  const regularDateLength = new Set(
    eventDates.filter((e) => e.type === 'REGULAR_DATE').map((eventDate) => eventDate.eventId),
  ).size;
  const appointmentsLength = eventDates.filter((e) => e.type === 'APPOINTMENT').length;

  // Sort by start date
  const sortedDates = [...eventDates].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

  // Helper to get YYYY-MM-DD key from Date
  const getDayKey = (d) => {
    const dt = new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  };

  const getUtcDayStamp = (d) => {
    const dt = new Date(d);
    return Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate());
  };

  const isRegularDateGroupCandidate = (eventDate) => eventDate.type === 'REGULAR_DATE' && !agencyModeActive;

  const canBeMerged = (current, previousInGroup) => {
    const currentDayStamp = getUtcDayStamp(current.start);
    const previousDayStamp = getUtcDayStamp(previousInGroup.start);
    const dayDifference = (currentDayStamp - previousDayStamp) / MS_IN_DAY;

    // Same day (multiple slots) or directly consecutive days belong to one group.
    return dayDifference === 0 || dayDifference === 1;
  };

  const groupedEvents = [];
  const groupCandidatesByEventId = sortedDates.reduce((map, eventDate) => {
    if (!isRegularDateGroupCandidate(eventDate)) {
      groupedEvents.push([eventDate]);
      return map;
    }

    const existingEventDates = map[eventDate.eventId] ?? [];
    map[eventDate.eventId] = [...existingEventDates, eventDate];
    return map;
  }, {});

  Object.values(groupCandidatesByEventId).forEach((eventDatesOfEventId) => {
    const eventDatesSorted = [...eventDatesOfEventId].sort(
      (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
    );

    const eventGroups = [];
    eventDatesSorted.forEach((eventDate) => {
      const lastGroup = eventGroups[eventGroups.length - 1];
      if (!lastGroup || !canBeMerged(eventDate, lastGroup[lastGroup.length - 1])) {
        eventGroups.push([eventDate]);
        return;
      }
      lastGroup.push(eventDate);
    });

    groupedEvents.push(...eventGroups);
  });

  groupedEvents.sort((a, b) => new Date(a[0].start).getTime() - new Date(b[0].start).getTime());

  // Build components
  const eventsOfMonth = groupedEvents.map((dates) =>
    dates.length === 1 ? (
      <EventItem key={dates[0].id} bookingEventDate={dates[0]} />
    ) : (
      <EventItemMultiple
        key={`${dates[0].eventId}-${getDayKey(dates[0].start)}-${getDayKey(dates[dates.length - 1].start)}`}
        bookingEventDates={dates}
      />
    ),
  );

  return (
    <div className="events-month-indent">
      <Box
        p="md"
        mt={15}
        sx={(theme) => ({
          backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[9] : theme.colors.gray[0],
          borderRadius: 10,
        })}
      >
        <Group>
          <ThemeText size="lg" weight="bold">{monthTitle}</ThemeText>
          {regularDateLength > 0 && <Text size="xs" color="dimmed">{`${regularDateLength} Events`}</Text>}
          {!agencyModeActive && appointmentsLength > 0 && (
            <Text size="xs" color="dimmed">{`${appointmentsLength} Termine`}</Text>
          )}
        </Group>
      </Box>
      <Stack mt="sm">{eventsOfMonth}</Stack>
    </div>
  );
};

export default EventsMonthPane;
